import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useAuth } from "@/context/AuthContext";
import { Plus, PaperPlaneTilt, Trash, Eye } from "@phosphor-icons/react";
import NewPacketModal from "@/components/NewPacketModal";
import ReviewModal from "@/components/ReviewModal";
import { toast } from "sonner";

const REVIEWABLE = ["pending_review", "changes_requested"];
const FILTERS = [
  { key: "all", label: "All" },
  { key: "review", label: "Needs review", match: (p) => REVIEWABLE.includes(p.status) },
  { key: "sent", label: "Sent", match: (p) => p.status === "sent" },
  { key: "completed", label: "Signed & returned", match: (p) => p.status === "completed" },
];

export default function Packets() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  const canCreate = ["admin", "manager"].includes(user.role);
  const [params, setParams] = useSearchParams();
  const filter = FILTERS.some((f) => f.key === params.get("filter")) ? params.get("filter") : "all";
  const [rows, setRows] = useState([]);
  const [reps, setReps] = useState([]);
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [reviewFor, setReviewFor] = useState(null);
  const repName = (id) => reps.find((r) => r.id === id)?.name || "";

  const load = () => api.get("/contractor-packets").then((r) => setRows(Array.isArray(r.data) ? r.data : []));
  useEffect(() => {
    load();
    if (["admin", "analyst"].includes(user.role)) api.get("/sales-reps").then((r) => setReps(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, [user.role]);
  const setFilter = (k) => setParams(k === "all" ? {} : { filter: k }, { replace: true });
  const shown = useMemo(() => { const f = FILTERS.find((x) => x.key === filter); return f?.match ? rows.filter(f.match) : rows; }, [rows, filter]);

  const resend = async (id) => {
    setBusyId(id);
    try {
      const { data } = await api.post(`/contractor-packets/${id}/resend`);
      toast.success(`Resent to ${data.to}.`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    } finally { setBusyId(null); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete this packet for ${p.worker_email}?\n\nThis cannot be undone.${p.status === "completed" ? " The worker's submitted data will be permanently erased." : ""}`)) return;
    try {
      await api.delete(`/contractor-packets/${p.id}`);
      toast.success("Packet deleted.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString() || e.message);
    }
  };

  return (
    <div className="p-4 md:p-8">
      <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="tinylabel">Recruit &amp; onboard</div>
          <h1 className="text-3xl font-black">Onboarding Packets</h1>
          <p className="text-slate-500 mt-1 text-sm">The paperwork a worker fills in before an assignment — contact, tax ID and direct-deposit details — sent as a secure link. Link a packet to a project and the assignment (plus the worker's login notice) is created automatically when they sign.</p>
        </div>
        {canCreate && (
          <button data-testid="new-packet-btn" onClick={() => setOpen(true)}
            className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-md flex items-center gap-2">
            <Plus size={18} weight="bold" /> New Packet / Assignment
          </button>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3" data-testid="packets-filters">
        {FILTERS.map((f) => {
          const n = f.match ? rows.filter(f.match).length : rows.length;
          const active = filter === f.key;
          return (
            <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`packet-filter-${f.key}`}
                    className={`h-9 px-3 rounded-full text-sm font-semibold border inline-flex items-center gap-1.5 ${active ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"}`}>
              {f.label}
              <span className={`text-[11px] px-1.5 rounded-full ${active ? "bg-white/20" : f.key === "review" && n > 0 ? "bg-amber-400 text-amber-950" : "bg-slate-100 text-slate-600"}`} data-testid={`packet-filter-count-${f.key}`}>{n}</span>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Worker email</th>
              <th className="text-left px-4 py-3">Client</th>
              <th className="text-left px-4 py-3">Sent</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {shown.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                {rows.length === 0 ? 'No packets yet. Click "New Packet / Assignment" to send one.' : "Nothing matches this filter."}
              </td></tr>
            )}
            {shown.map((p) => (
              <tr key={p.id} className={`border-t border-slate-100 hover:bg-slate-50 ${REVIEWABLE.includes(p.status) ? "bg-amber-50/40" : ""}`} data-testid={`packet-row-${p.id}`}>
                <td className="px-4 py-3 font-medium">{p.worker_email}{p.worker_name && <div className="text-xs text-slate-500">{p.worker_name}</div>}
                  {p.sales_rep_id && <div className="text-[11px] text-emerald-700 font-semibold" data-testid={`packet-rep-${p.id}`}>Rep: {repName(p.sales_rep_id) || p.submitted_by || "sales rep"}</div>}
                </td>
                <td className="px-4 py-3">{p.company_name}<div className="text-xs text-slate-500">{p.location}</div>
                  {p.project_id && <div className="text-[11px] text-emerald-700 font-semibold" data-testid={`packet-project-${p.id}`}>↳ {p.project_name || "Linked project"}{p.auto_assign && !p.assignment_id_created ? " · auto-assign" : ""}</div>}
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs whitespace-nowrap">{REVIEWABLE.includes(p.status) ? <span className="text-slate-400">not sent</span> : (p.sent_at || p.created_at || "").slice(0, 10)}</td>
                <td className="px-4 py-3"><StatusPill status={p.status} />
                  {p.status === "changes_requested" && p.review_note && <div className="text-xs text-orange-800 italic mt-1 max-w-[220px] truncate" title={p.review_note}>“{p.review_note}”</div>}
                  {p.assignment_id_created && (
                    <span data-testid={`packet-assigned-badge-${p.id}`} title={`Assignment created on ${p.project_name}`}
                          className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-emerald-100 text-emerald-800 border-emerald-300">assigned</span>
                  )}
                  {p.auto_assign_status === "held" && !p.assignment_id_created && (
                    <span data-testid={`packet-held-badge-${p.id}`} title={p.auto_assign_error}
                          className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-amber-100 text-amber-800 border-amber-300">needs review</span>
                  )}
                  {p.packet_email_failed && p.status === "sent" && (
                    <span data-testid={`packet-bounce-badge-${p.id}`}
                          title={`Reminder email to ${p.packet_email_failed.to || p.worker_email} could not be delivered (${String(p.packet_email_failed.at || "").slice(0, 10)}). Check the address — edit the packet or delete and re-send.`}
                          className="ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border bg-rose-100 text-rose-800 border-rose-300">
                      email bounced
                    </span>
                  )}
                  {(() => {
                    const stages = p.packet_reminders_sent || [];
                    const failed = p.packet_reminders_failed || [];
                    if (p.status !== "sent" || (stages.length === 0 && failed.length === 0)) return null;
                    const total = 3;
                    const attempted = stages.length + failed.length;
                    const isGhosted = attempted >= total;
                    return (
                      <span data-testid={`packet-reminder-badge-${p.id}`}
                            title={isGhosted
                              ? `${stages.length} reminder${stages.length === 1 ? "" : "s"} delivered${failed.length ? `, ${failed.length} undeliverable` : ""} — no response. Reminders stop after 3 attempts; consider deleting.`
                              : `${stages.length} of 3 reminders sent${failed.length ? ` (${failed.length} failed)` : ""} — stops after 3 attempts`}
                            className={`ml-2 inline-block text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded border ${
                              isGhosted
                                ? "bg-rose-50 text-rose-700 border-rose-200"
                                : "bg-amber-50 text-amber-700 border-amber-200"
                            }`}>
                        {attempted}/{total} {isGhosted ? "ghosted" : "nudged"}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <Link to={`/packets/${p.id}`} data-testid={`packet-open-${p.id}`} className="text-green-600 hover:text-green-800 font-semibold text-xs mr-3">Open</Link>
                  {isAdmin && REVIEWABLE.includes(p.status) && (
                    <button onClick={() => setReviewFor(p)} data-testid={`packet-review-${p.id}`}
                      title="Approve & send to the worker, or send back to the rep with a note"
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold align-middle mr-3">
                      <Eye size={13} weight="bold" /> Review
                    </button>
                  )}
                  {isAdmin && p.status === "sent" && (
                    <button onClick={() => resend(p.id)} disabled={busyId === p.id}
                      data-testid={`packet-resend-${p.id}`}
                      className="text-slate-600 hover:text-slate-900 font-semibold text-xs mr-3 disabled:opacity-60">
                      <PaperPlaneTilt size={12} weight="bold" className="inline mr-1" />
                      {busyId === p.id ? "Sending…" : "Resend"}
                    </button>
                  )}
                  {isAdmin && (
                    <button onClick={() => del(p)} data-testid={`packet-delete-${p.id}`}
                      title="Delete packet" className="text-slate-400 hover:text-rose-600 align-middle">
                      <Trash size={14} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && <NewPacketModal onClose={() => setOpen(false)} onSent={load} />}
      {reviewFor && <ReviewModal kind="packet" doc={reviewFor} repName={repName(reviewFor.sales_rep_id) || reviewFor.submitted_by} onClose={() => setReviewFor(null)} onDone={load} />}
    </div>
  );
}
